import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

/** The Supabase auth session, kept in sync with sign-in and token refresh. */
export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // getSession() resolves from storage, so a returning user never sees the
    // sign-in screen flash before their session is restored.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

/** Is the signed-in user on the allowlist?
 *
 *  The check is simply "can I read any row of sacrament_allowed_emails". RLS
 *  makes that table return zero rows to anyone not on it, so a non-empty result
 *  *is* the authorization check — there is nothing to compare client-side, and
 *  nothing a client could forge.
 */
export function useAccess(enabled: boolean) {
  return useQuery({
    queryKey: ["access"],
    enabled,
    // Access changes only when a row is added or removed in the dashboard, so
    // there is no point re-checking it on every window focus.
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from("sacrament_allowed_emails")
        .select("email")
        .limit(1);
      if (error) throw error;
      return (data ?? []).length > 0;
    },
  });
}
